import type { ComponentChildren } from 'preact';
import type { LogClient, LogDescriptor } from '../../core/log/client';
import { shortLogName } from '../../core/loglist/slug';
import { href, withFrom } from '../../router';
import { fmtInt } from '../format';
import { LOG_TAB_LABEL, logPath, operatorPath, type LogTab } from '../nav';
import { treeHeads } from '../state/treeHeads';
import { Breadcrumbs, type Crumb } from './Breadcrumbs';
import { Hash } from './Hash';
import { Icon } from './Icon';
import { KindBadge, ProgramBadges, ReachBadge } from './LogBadges';
import { Tabs } from './Tabs';
import { UrlCompact } from './UrlCompact';
import { isDesktop } from '../state/desktop';

export type { LogTab };

/**
 * Header + function tabs for every log page (the sidebar lives in the app shell).
 * The breadcrumb is All logs › Operator › Log, plus whatever `trail` the page adds (e.g. Entries › #1,000).
 */
export function LogLayout({ log, client, tab, trail = [], children }: { log: LogDescriptor; client?: LogClient; tab: LogTab; trail?: Crumb[]; children: ComponentChildren }) {
  const th = treeHeads.value[log.id];
  const crumbs: Crumb[] = [
    { label: 'All logs', href: href('/logs') },
    { label: log.operator, href: operatorPath(log.operator) },
    { label: shortLogName(log), href: logPath(log) },
    ...trail,
  ];
  return (
    <div class="content">
      <div class="log-head">
        <Breadcrumbs items={crumbs} />
        <div class="title-row">
          <h1>{log.description}</h1>
          <KindBadge kind={log.kind} />
          <ProgramBadges log={log} />
          {!isDesktop() && <ReachBadge logId={log.id} />}
        </div>
        <div class="meta">
          <span>
            <Icon name="link" size={13} /> <code class="url-inline"><UrlCompact url={log.kind === 'tiled' ? log.monitoringUrl : log.url} /></code>
          </span>
          {th && (
            <span>
              <Icon name="tree" size={13} /> {fmtInt(th.treeSize)} entries
            </span>
          )}
          {log.logId && (
            <span class="row" style="gap:0.3rem">
              <Icon name="key" size={13} /> <Hash value={log.logId} short={6} />
            </span>
          )}
        </div>
      </div>
      <Tabs
        items={[
          { label: LOG_TAB_LABEL.overview, href: logPath(log), active: tab === 'overview', icon: 'info' },
          { label: LOG_TAB_LABEL.entries, href: logPath(log, 'entries'), active: tab === 'entries', icon: 'list' },
          { label: LOG_TAB_LABEL.inclusion, href: logPath(log, 'inclusion'), active: tab === 'inclusion', icon: 'shield', title: 'Inclusion proof' },
          { label: LOG_TAB_LABEL.consistency, href: logPath(log, 'consistency'), active: tab === 'consistency', icon: 'tree', title: 'Consistency proof' },
          // Submitting is a global tool: this tab hands over to it with the log pre-ticked (same as the Overview card).
          { label: 'Submit', href: withFrom(href('/submit', { log: log.id })), active: false, icon: 'upload', title: 'Submit a chain to this log (opens the Submit page with this log pre-ticked)' },
        ]}
      />
      {client === undefined ? <div class="errorbox">This log kind is not supported.</div> : children}
    </div>
  );
}
